import { Api } from '@/api/api'
import { toPortfolioItem } from '@/shared/utils/portfolio'
import { KonstanzHomeState, ProjectOfferLite } from '@/business/home/store/konstanz-home-state'
import { sortBy } from '@/shared/utils/sort-by'
import { isAfter, isBefore } from 'date-fns'
import { OfferEvent } from '@/api/generated'
import { CachedValue } from '@tf/cache'

export class HomeService {
  konstanz$ = new CachedValue<KonstanzHomeState>({
    key: 'home_konstanz',
    resolveData: () => this.fetchKonstanz(),
    ttl: 60 * 60 * 1000,
  })

  private fetchKonstanz(): Promise<KonstanzHomeState> {
    const offers$ = Api.Offers.getOffers({
      page: 1,
      pageSize: 1000,
      project: 'konstanz',
    })
      .then(response => response.items)
      .then(offers => {
        let offersWithEvent: ProjectOfferLite[] = []
        const offersWithoutEvent: ProjectOfferLite[] = []

        for (const offer of offers) {
          if (offer.events && offer.events.length > 0) {
            offersWithEvent.push(offer)
          } else {
            offersWithoutEvent.push(offer)
          }
        }

        offersWithEvent = offersWithEvent
          .map(offer => {
            const events: OfferEvent[] = offer.events!

            let nextEvent: OfferEvent = events.find(event => {
              return isAfter(event.dateFrom, new Date())
            })!

            if (nextEvent == null) {
              nextEvent = events[events.length - 1]
            }

            return {
              nextEvent,
              ...offer,
            }
          })
          .sort((a, b) => this.compareOffers(a.nextEvent?.dateFrom, b.nextEvent?.dateFrom))

        const sortedOffers = this.sortOffersWithEvent(offersWithEvent)

        return {
          currentOffers: sortedOffers.currentOffers,
          pastOffers: sortedOffers.pastOffers.concat(offersWithoutEvent),
        }
      })

    const organisations$ = Api.Organisations.getOrganisations({
      page: 1,
      pageSize: 1000,
      project: 'konstanz',
    })
      .then(response => response.items)
      .then(organisations => {
        return organisations.sort(sortBy(organisation => organisation.name.toLowerCase()))
      })

    return Promise.all([offers$, organisations$]).then(([offers, organisations]) => {
      return {
        currentOffers: offers.currentOffers,
        pastOffers: offers.pastOffers,
        organisations: organisations.map(toPortfolioItem('organisations', 'organisation')),
      }
    })
  }

  private sortOffersWithEvent(offers: ProjectOfferLite[]) {
    const currentOffers: ProjectOfferLite[] = []
    const pastOffers: ProjectOfferLite[] = []

    const now = new Date()

    for (const offer of offers) {
      if (offer.nextEvent && isBefore(offer.nextEvent.dateUntil, now)) {
        pastOffers.push(offer)
      } else {
        currentOffers.push(offer)
      }
    }

    currentOffers.sort((a, b) => this.compareOffers(a.nextEvent?.dateFrom, b.nextEvent?.dateFrom))
    pastOffers.sort((a, b) => this.compareOffers(a.nextEvent?.dateFrom, b.nextEvent?.dateFrom))

    return {
      currentOffers,
      pastOffers,
    }
  }

  private compareOffers(a?: Date, b?: Date) {
    return (a?.getTime() ?? 0) - (b?.getTime() ?? 0)
  }
}
